import { Rating, Surface } from '@nexvia-logistics/ui';
import { Star } from '@nexvia-logistics/ui/icons';
import type { StorageProfile } from './storage-api';

/** One decimal, es-UY ("4,5"): the API already rounds `averageRating` to one decimal (contract C1). */
function formatAverage(value: number): string {
  return value.toLocaleString('es-UY', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/**
 * Audit S5 (contract C1, NEX-1329) — what the clients rated this depósito, from `GET /storage/me`'s
 * `rating` (`client_to_storage` reviews only): the average with its stars and how many reviews it
 * comes from. Renders nothing when the profile carries no `rating` (an API before C1, or the
 * `PATCH /storage/me` response, which never computes it), so it never invents a "sin calificaciones".
 */
export function DepotRatingCard({ rating }: { rating: StorageProfile['rating'] }) {
  if (!rating) return null;
  const { averageRating, reviewCount } = rating;

  return (
    <Surface as="section" variant="outlined" padding="md">
      {/* NEX-1164 design pass (spec 2026-10-18 §3.6): no mock for this card; the `Star` glyph in the
          title, like the other cards of the page. */}
      <h2 className="m-0 flex items-center gap-2 text-ui-h3 text-on-surface">
        <Star size={20} className="shrink-0 text-primary" />
        Calificación de tus clientes
      </h2>
      {averageRating === null || reviewCount === 0 ? (
        <p className="mt-2 text-ui-body text-on-surface-variant">
          Todavía no tenés calificaciones. Tus clientes pueden calificarte cuando confirmás la recepción de sus cosas.
        </p>
      ) : (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className="text-ui-h3 text-on-surface">{formatAverage(averageRating)}</span>
          <Rating value={averageRating} label="Promedio de calificaciones" size={18} />
          <span className="text-ui-body text-on-surface-variant">
            {reviewCount === 1 ? '1 calificación' : `${reviewCount} calificaciones`}
          </span>
        </div>
      )}
    </Surface>
  );
}
